import { useMemo, useState } from 'react';

import { CustomHeadersEditor, type CustomHeaderEntry } from '../../../components/CustomHeadersEditor';
import {
  ProviderTypes,
  type CreatePlatformProviderRequest,
  type PlatformProviderResponse,
  type ProviderHeaderInput,
  type ProviderType,
  type UpdatePlatformProviderRequest,
} from '../api';
import { useProviderModels, useRefreshProviderModels } from '../hooks/useProviderModels';

type FormMode = 'create' | 'edit';

interface PlatformProviderFormProps {
  mode: FormMode;
  provider?: PlatformProviderResponse;
  busy: boolean;
  onClose: () => void;
  onCreate?: (request: CreatePlatformProviderRequest) => void;
  onUpdate?: (request: UpdatePlatformProviderRequest) => void;
}

const TYPES = Object.values(ProviderTypes);

export function PlatformProviderForm({ mode, provider, busy, onClose, onCreate, onUpdate }: PlatformProviderFormProps): JSX.Element {
  const [providerType, setProviderType] = useState<ProviderType>(provider?.providerType ?? 'OpenAiCloud');
  const isOllama = providerType === 'OllamaLocal';
  const isRelay = providerType === 'OllamaViaAzureRelay';
  const requiresEndpoint = isOllama || isRelay || providerType === 'AzureOpenAi' || providerType === 'ContentUnderstanding';
  const requiresApiKey = !isOllama;
  const [displayName, setDisplayName] = useState(provider?.displayName ?? '');
  const [modelName, setModelName] = useState(provider?.modelName ?? '');
  const [endpoint, setEndpoint] = useState(provider?.endpoint ?? '');
  const [apiKey, setApiKey] = useState('');
  const [headers, setHeaders] = useState<CustomHeaderEntry[]>(
    () => (provider?.customHeaders ?? []).map((h) => ({ name: h.name, value: h.value, isSecret: h.isSecret })),
  );
  const [isClassificationDefault, setIsClassificationDefault] = useState(
    provider?.isClassificationDefault ?? false,
  );

  // Track which secret rows came from the backend already stored in Key Vault. In edit mode we don't
  // ship their value again unless the operator types a fresh one — saves an unnecessary KV write
  // and avoids accidentally overwriting a working secret with the placeholder.
  const storedSecretRefs = useMemo(() => {
    const refs = new Set<string>();
    for (const h of provider?.customHeaders ?? []) {
      if (h.isSecret) refs.add(h.value);
    }
    return refs;
  }, [provider]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const customHeaders = serializeHeaders(headers, storedSecretRefs);
    if (mode === 'create') {
      onCreate?.({
        providerType,
        displayName,
        modelName,
        endpoint: endpoint.trim() || null,
        apiKeyPlaintext: apiKey,
        ...(customHeaders.length > 0 ? { customHeaders } : {}),
        ...(isClassificationDefault ? { isClassificationDefault: true } : {}),
      });
    } else {
      const request: UpdatePlatformProviderRequest = {};
      if (displayName !== provider?.displayName) request.displayName = displayName;
      if (modelName !== provider?.modelName) request.modelName = modelName;
      const ep = endpoint.trim() || null;
      if (ep !== (provider?.endpoint ?? null)) request.endpoint = ep;
      // Only ship the key when the admin typed a new one — empty input keeps the current secret.
      if (apiKey.trim() !== '') request.apiKeyPlaintext = apiKey;
      // Headers ship whenever the editor was touched (different length, different entries, or a
      // secret value changed). We compare against the original list to avoid sending no-op replacements.
      if (headersChanged(provider?.customHeaders ?? [], headers)) {
        request.customHeaders = customHeaders;
      }
      // Ship the classification-default flag only when it changed (true clears it from every other provider).
      if (isClassificationDefault !== (provider?.isClassificationDefault ?? false)) {
        request.isClassificationDefault = isClassificationDefault;
      }
      onUpdate?.(request);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ppf-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
      >
        <h2 id="ppf-title" className="text-lg font-semibold text-slate-900">
          {mode === 'create' ? 'Añadir provider de plataforma' : 'Editar provider'}
        </h2>

        <div className="mt-5 space-y-4">
          {mode === 'create' ? (
            <div>
              <label className="label" htmlFor="ppf-type">Tipo</label>
              <select id="ppf-type" className="input" value={providerType} onChange={(e) => setProviderType(e.target.value as ProviderType)} disabled={busy}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          ) : null}
          <div>
            <label className="label" htmlFor="ppf-name">Nombre</label>
            <input id="ppf-name" className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={busy} required />
          </div>
          <ModelField
            mode={mode}
            provider={provider}
            modelName={modelName}
            onChange={setModelName}
            busy={busy}
          />
          <div>
            <label className="label" htmlFor="ppf-endpoint">
              Endpoint {requiresEndpoint ? '' : '(opcional)'}
            </label>
            <input
              id="ppf-endpoint"
              className="input"
              placeholder={
                isRelay
                  ? 'relay+wss://relaydgx.servicebus.windows.net/ollama?keyName=sender'
                  : isOllama ? 'http://ollama.local:11434' : 'https://…'
              }
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              disabled={busy}
              required={isOllama || isRelay}
            />
            {isOllama ? (
              <p className="mt-1 text-xs text-slate-500">Ollama es self-hosted: la URL debe ser alcanzable desde Functions.</p>
            ) : null}
            {isRelay ? (
              <p className="mt-1 text-xs text-slate-500">
                Formato <code>relay+wss://&lt;namespace&gt;/&lt;hybrid-path&gt;?keyName=&lt;ruleName&gt;</code>.
                La SAS key se introduce abajo (campo &quot;API key&quot;).
              </p>
            ) : null}
          </div>
          {mode === 'create' && requiresApiKey ? (
            <div>
              <label className="label" htmlFor="ppf-key">{isRelay ? 'SAS key (relay)' : 'API key'}</label>
              <input id="ppf-key" type="password" autoComplete="new-password" className="input" value={apiKey} onChange={(e) => setApiKey(e.target.value)} disabled={busy} required />
              <p className="mt-1 text-xs text-slate-500">
                {isRelay
                  ? 'Clave de la regla SAS del relay (la introducida en `keyName`). Se almacena en Key Vault.'
                  : 'Se almacena en Key Vault.'}
              </p>
            </div>
          ) : mode === 'edit' ? (
            <div>
              <label className="label" htmlFor="ppf-rotate-key">{isRelay ? 'Rotar SAS key (opcional)' : 'Rotar API key (opcional)'}</label>
              <input
                id="ppf-rotate-key"
                type="password"
                autoComplete="new-password"
                className="input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                disabled={busy}
                placeholder="Dejar vacío para conservar la actual"
              />
              <p className="mt-1 text-xs text-slate-500">Si introduces un valor, sobrescribe el secreto en Key Vault.</p>
            </div>
          ) : null}

          <CustomHeadersEditor
            value={headers}
            busy={busy}
            onChange={setHeaders}
            storedSecretRefs={storedSecretRefs}
          />

          <div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isClassificationDefault}
                onChange={(e) => setIsClassificationDefault(e.target.checked)}
                disabled={busy}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                data-testid="ppf-classification-default"
              />
              <span>
                <span className="font-medium text-slate-900">Proveedor de clasificación por defecto</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  El modelo que usa la clasificación automática de tipo. Solo uno en toda la plataforma;
                  marcarlo lo quita de cualquier otro. Debe ser un LLM (no Content Understanding).
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Guardando…' : mode === 'create' ? 'Crear' : 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
}

/**
 * Translates the editor's free-form rows into the request payload. Secret rows whose value still
 * matches a KV reference present at load time are left untouched — sending the ref string back as
 * a plaintext value would write garbage into Key Vault. The backend interprets "secret entry with
 * the same value as on the server" as an idempotent no-op.
 */
function serializeHeaders(
  entries: CustomHeaderEntry[],
  storedRefs: ReadonlySet<string>,
): ProviderHeaderInput[] {
  return entries
    .filter((h) => h.name.trim() !== '' && (h.value !== '' || storedRefs.has(h.value)))
    .map<ProviderHeaderInput>((h) => ({ name: h.name.trim(), value: h.value, isSecret: h.isSecret }));
}

/**
 * Best-effort diff between the original header list (as returned by the API) and the editor state.
 * Returns true when we should ship a replacement payload. Avoids sending no-op replacements that
 * would queue unnecessary KV deletes for unchanged secret rows.
 */
function headersChanged(
  original: ReadonlyArray<{ name: string; value: string; isSecret: boolean }>,
  current: CustomHeaderEntry[],
): boolean {
  if (original.length !== current.length) return true;
  for (let i = 0; i < original.length; i++) {
    const a = original[i] as { name: string; value: string; isSecret: boolean };
    const b = current[i] as CustomHeaderEntry;
    if (a.name !== b.name || a.isSecret !== b.isSecret) return true;
    // For non-secrets the literal value must match; secrets compare KV refs.
    if (a.value !== b.value) return true;
  }
  return false;
}

/**
 * Model identifier field. In <c>create</c> mode it stays free-text because the provider doesn't
 * exist yet (no id to probe against). In <c>edit</c> mode the admin can click "Cargar modelos
 * disponibles", which calls <c>GET /admin/platform-providers/{id}/models</c> and renders a
 * dropdown. The free-text input remains as an escape hatch (Azure deployments with custom names,
 * Content Understanding analyzers, etc.).
 */
interface ModelFieldProps {
  mode: FormMode;
  provider: PlatformProviderResponse | undefined;
  modelName: string;
  onChange: (value: string) => void;
  busy: boolean;
}

function ModelField({ mode, provider, modelName, onChange, busy }: ModelFieldProps): JSX.Element {
  const providerId = mode === 'edit' && provider ? provider.id : null;
  const [loadRequested, setLoadRequested] = useState(false);
  const models = useProviderModels(providerId, { enabled: loadRequested });
  const refresh = useRefreshProviderModels();

  const isLoading = loadRequested && (models.isLoading || refresh.isPending);
  const cache = models.data;
  const fetchedModels = useMemo(() => cache?.models ?? [], [cache]);
  const inDropdown = useMemo(
    () => fetchedModels.some((m) => m.modelId === modelName),
    [fetchedModels, modelName],
  );

  const handleLoad = (): void => {
    setLoadRequested(true);
  };

  const handleForceRefresh = (): void => {
    if (!providerId) return;
    refresh.mutate(providerId, {
      onSuccess: () => {
        setLoadRequested(true);
      },
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="label" htmlFor="ppf-model">Modelo / deployment</label>
        {mode === 'edit' && providerId ? (
          <button
            type="button"
            className="text-xs font-medium text-violet-700 hover:underline disabled:opacity-50"
            onClick={loadRequested ? handleForceRefresh : handleLoad}
            disabled={busy || isLoading}
          >
            {isLoading
              ? 'Cargando…'
              : loadRequested
                ? 'Refrescar modelos'
                : 'Cargar modelos disponibles'}
          </button>
        ) : null}
      </div>
      <input
        id="ppf-model"
        className="input"
        value={modelName}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        required
        list={loadRequested && fetchedModels.length > 0 ? 'ppf-model-options' : undefined}
      />
      {loadRequested && fetchedModels.length > 0 ? (
        <datalist id="ppf-model-options">
          {fetchedModels.map((m) => (
            <option key={m.modelId} value={m.modelId}>
              {m.displayName ?? m.modelId}
            </option>
          ))}
        </datalist>
      ) : null}
      {loadRequested && cache?.notApplicable ? (
        <p className="mt-1 text-xs text-slate-500">
          Este provider no expone un catálogo de modelos — escribe el id manualmente.
        </p>
      ) : null}
      {loadRequested && cache?.fetchError ? (
        <p className="mt-1 text-xs text-rose-600">
          Error consultando upstream: {cache.fetchError}. Puedes escribir el modelo a mano.
        </p>
      ) : null}
      {loadRequested && !cache?.notApplicable && !cache?.fetchError && fetchedModels.length > 0 && !inDropdown && modelName.trim() !== '' ? (
        <p className="mt-1 text-xs text-amber-600">
          Valor custom (no aparece en la lista del provider).
        </p>
      ) : null}
    </div>
  );
}
